import { CSElementPlugin, LifecycleHooks } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { DevToolsManager } from '../devtools/DevToolsManager';

/**
 * Плагин DevTools для CSElement
 * Обеспечивает инспектирование и профилирование элементов
 */
export class DevToolsPlugin implements CSElementPlugin {
  readonly name = 'devtools';
  readonly version = '1.0.0';
  readonly description = 'Инструменты разработчика для инспектирования и профилирования CSElement';

  private manager: DevToolsManager;
  private isInstalled = false;
  private CSElementClass: any = null;

  constructor() {
    this.manager = new DevToolsManager();
  }

  /**
   * Хуки жизненного цикла
   */
  hooks: LifecycleHooks = {
    afterCreate: (element: ICSElement) => {
      // Автоматически регистрируем новые элементы
      this.manager.registerElement(element);
    },

    beforeDestroy: (element: ICSElement) => {
      // Отменяем регистрацию при уничтожении элемента
      this.manager.unregisterElement(element);
    }
  };

  /**
   * Установка плагина
   */
  install(CSElementClass?: any): void {
    if (this.isInstalled) {
      console.warn('🔧 Плагин DevTools уже установлен');
      return;
    }

    // Сохраняем ссылку на CSElementClass
    this.CSElementClass = CSElementClass;

    // Расширяем CSElement методами DevTools
    this.extendCSElement();

    // Регистрируем хуки жизненного цикла если передан CSElementClass
    if (CSElementClass) {
      const pluginManager = CSElementClass.plugins;
      
      if (this.hooks.afterCreate) {
        pluginManager.addHook('afterCreate', this.hooks.afterCreate);
      }
      
      if (this.hooks.beforeDestroy) {
        pluginManager.addHook('beforeDestroy', this.hooks.beforeDestroy);
      }
    } else {
      console.log('⚠️ CSElementClass не передан, хуки не регистрируются');
    }

    this.isInstalled = true;
    console.log('🔧 Плагин DevTools установлен');
  }

  /**
   * Удаление плагина
   */
  uninstall(): void {
    if (!this.isInstalled) {
      console.warn('🔧 Плагин DevTools не установлен');
      return;
    }

    // Удаляем методы из прототипа
    this.removeCSElementExtensions();

    // Удаляем хуки жизненного цикла если есть сохраненная ссылка на CSElementClass
    if (this.CSElementClass) {
      const pluginManager = this.CSElementClass.plugins;
      
      if (this.hooks.afterCreate) {
        pluginManager.removeHook('afterCreate', this.hooks.afterCreate);
      }
      
      if (this.hooks.beforeDestroy) {
        pluginManager.removeHook('beforeDestroy', this.hooks.beforeDestroy);
      }
    }

    // Очищаем ссылку на CSElementClass
    this.CSElementClass = null;

    this.isInstalled = false;
    console.log('🔧 Плагин DevTools удален');
  }

  /**
   * Получить менеджер DevTools
   */
  getManager(): DevToolsManager {
    return this.manager;
  }

  /**
   * Расширить CSElement методами DevTools
   */
  private extendCSElement(): void {
    // Используем сохраненную ссылку на CSElementClass
    const CSElementClass = this.CSElementClass;

    if (!CSElementClass) return;

    // Метод инспектирования элемента
    CSElementClass.prototype.inspect = function() {
      return devToolsPlugin.manager.inspector.inspect(this);
    };

    // Метод создания снимка структуры
    CSElementClass.prototype.createSnapshot = function() {
      return devToolsPlugin.manager.inspector.createSnapshot(this);
    };

    // Используем встроенную сериализацию CSElement - метод serialize уже есть
    // Добавляем метод для DevTools-специфичной сериализации
    CSElementClass.prototype.serializeForDevTools = function(options?: any) {
      return devToolsPlugin.manager.inspector.serialize(this, options);
    };

    // Метод поиска по пути
    CSElementClass.prototype.findByPath = function(path: string[]) {
      return devToolsPlugin.manager.inspector.findByPath(this, path);
    };

    // Метод получения пути элемента
    CSElementClass.prototype.getElementPath = function() {
      return devToolsPlugin.manager.inspector.getElementPath(this);
    };

    // Статические методы
    CSElementClass.getDevToolsManager = function() {
      return devToolsPlugin.manager;
    };

    CSElementClass.runDiagnostics = function() {
      return devToolsPlugin.manager.runDiagnostics();
    };

    CSElementClass.exportDevToolsData = function(options?: any) {
      return devToolsPlugin.manager.exportData(options);
    };

    CSElementClass.importDevToolsData = function(data: any) {
      return devToolsPlugin.manager.importData(data);
    };

    // Методы профилирования
    CSElementClass.startProfiling = function(operationType: string, metadata?: any) {
      return devToolsPlugin.manager.profiler.startMeasurement(operationType, metadata);
    };

    CSElementClass.endProfiling = function(measurementId: string) {
      return devToolsPlugin.manager.profiler.endMeasurement(measurementId);
    };

    CSElementClass.getProfilerReport = function(timeRange?: any) {
      return devToolsPlugin.manager.profiler.getReport(timeRange);
    };
  }

  /**
   * Удалить расширения CSElement
   */
  private removeCSElementExtensions(): void {
    // Используем сохраненную ссылку на CSElementClass
    const CSElementClass = this.CSElementClass;

    if (!CSElementClass) return;

    // Удаляем методы экземпляра
    delete CSElementClass.prototype.inspect;
    delete CSElementClass.prototype.createSnapshot;
    delete CSElementClass.prototype.serializeForDevTools;
    delete CSElementClass.prototype.findByPath;
    delete CSElementClass.prototype.getElementPath;

    // Удаляем статические методы
    delete CSElementClass.getDevToolsManager;
    delete CSElementClass.runDiagnostics;
    delete CSElementClass.exportDevToolsData;
    delete CSElementClass.importDevToolsData;
    delete CSElementClass.startProfiling;
    delete CSElementClass.endProfiling;
    delete CSElementClass.getProfilerReport;
  }
}

// Экспортируем экземпляр плагина
export const devToolsPlugin = new DevToolsPlugin();

// Расширяем интерфейс ICSElement для TypeScript
declare module '../types/interfaces' {
  interface ICSElement {
    /**
     * Инспектировать элемент
     */
    inspect?(): any;

    /**
     * Создать снимок структуры
     */
    createSnapshot?(): any;

    /**
     * Сериализовать элемент для DevTools
     */
    serializeForDevTools?(options?: any): any;

    /**
     * Найти элемент по пути
     */
    findByPath?(path: string[]): ICSElement | null;

    /**
     * Получить путь элемента
     */
    getElementPath?(): string[];
  }
} 